import React, { useState } from 'react';
import { PageHeader, Card, FormGroup, Input, Select, Textarea, Button, DataTable, ActionButtons, SectionDivider } from '../../components/common/UIComponents';
import { AccountGroupController } from '../../controllers';
import { AccountHeads } from '../../models';
import { useAsyncData } from '../../hooks/useAsyncData';

export default function AccountGroupPage() {
  const { data: records, loading, error, reload } = useAsyncData(() => AccountGroupController.getAll());
  const [form, setForm]     = useState({ head: '', group: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  const handleSubmit = async () => {
    if (!form.head || !form.group) return;
    setSaving(true); setSaveErr(null);
    try {
      if (editing) {
        await AccountGroupController.update(editing, form);
        setEditing(null);
      } else {
        await AccountGroupController.add(form);
      }
      setForm({ head: '', group: '', description: '' });
      reload();
    } catch (e) { setSaveErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (row) => {
    setForm({ head: row.head || row.accountHead, group: row.group || row.groupName, description: row.description });
    setEditing(row.id);
  };

  const handleDelete = async (id) => {
    try { await AccountGroupController.delete(id); reload(); }
    catch (e) { alert(e.message); }
  };

  const columns = [
    { key: 'id', label: '#' },
    { key: 'accountHead', label: 'Account Head', render: r => r.accountHead || r.head },
    { key: 'groupName',   label: 'Account Group', render: r => r.groupName || r.group },
    { key: 'description', label: 'Description' },
    { key: 'actions', label: 'Actions', render: row => (
      <ActionButtons onEdit={() => handleEdit(row)} onDelete={() => handleDelete(row.id)} />
    )},
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Set Account Group" description="Create and manage account groups under each head" />
      <Card title={editing ? 'Edit Account Group' : 'Create Account Group'} style={{ marginBottom: 20 }}>
        <div className="form-grid form-grid-2" style={{ marginBottom: 14 }}>
          <FormGroup label="Select Account Head" required>
            <Select value={form.head} onChange={e => setForm(p => ({ ...p, head: e.target.value }))}
              options={AccountHeads} placeholder="Select" />
          </FormGroup>
          <FormGroup label="Group Name" required>
            <Input placeholder="Enter Group Name" value={form.group}
              onChange={e => setForm(p => ({ ...p, group: e.target.value }))} />
          </FormGroup>
        </div>
        <FormGroup label="Description" style={{ marginBottom: 14 }}>
          <Textarea placeholder="Enter Description" rows={2} value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
        </FormGroup>
        {saveErr && <div style={{ color: '#c62828', fontSize: 13, marginBottom: 8 }}>{saveErr}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : editing ? 'Update Group' : 'Create Group'}</Button>
          {editing && <Button variant="ghost" onClick={() => { setForm({ head: '', group: '', description: '' }); setEditing(null); }}>Cancel</Button>}
        </div>
      </Card>
      <SectionDivider />
      {loading && <div style={{ padding: 20, color: '#78909c' }}>Loading…</div>}
      {error   && <div style={{ padding: 20, color: '#c62828' }}>Error: {error}</div>}
      {!loading && <DataTable title="Account Group Details" columns={columns} data={records} />}
    </div>
  );
}
